import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownSelectTrigger,
} from '../../../components-v2/Dropdown';
import { styleSheet } from '../styles';
import { CalendarViewMode } from '../types';
import { ACTION_ITEM_COLOR, EVENT_COLOR, EXTERNAL_MEETING_COLOR, MEETING_LEGEND_COLOR } from '../utils/normalize';
import { CalendarUserSelect } from './CalendarUserSelect';
import { CalendarViewToolbar } from './CalendarViewToolbar';

interface IProps {
	viewMode: CalendarViewMode;
	onViewModeChange: (mode: CalendarViewMode) => void;
	title: string;
	onPrevious: () => void;
	onNext: () => void;
	onToday: () => void;
	currentUser: Api.IUser | null;
	selectedUser: Api.IUser | null;
	onUserChange: (user: Api.IUser | null) => void;
	/** Admins with scheduler reporting can view other users; others are locked to themselves. */
	canBrowseAll: boolean;
	/** Users who delegated meeting admin to the current user — selectable even without `canBrowseAll`. */
	delegatedUsers: Api.IUser[];
	showMeetings: boolean;
	onToggleMeetings: () => void;
	showEvents: boolean;
	onToggleEvents: () => void;
	/** Whether the events legend/toggle should be shown at all (Surveys feature on). */
	eventsAvailable: boolean;
	showActionItems: boolean;
	onToggleActionItems: () => void;
	showExternalMeetings: boolean;
	onToggleExternalMeetings: () => void;
}

/** A show/hide checkbox for one item type, carrying the type's legend color as a dot. */
const TypeCheckboxItem: React.FC<{ label: string; color: string; checked: boolean; onToggle: () => void }> = ({
	label,
	color,
	checked,
	onToggle,
}) => (
	<DropdownCheckboxItem checked={checked} onCheckedChange={onToggle}>
		<span className={css(styleSheet.legendDot)} style={{ backgroundColor: color }} />
		{label}
	</DropdownCheckboxItem>
);

/** Header controls for the standalone calendar: shared nav/switcher plus host filter and type legend. */
export const CalendarToolbar: React.FC<IProps> = ({
	viewMode,
	onViewModeChange,
	title,
	onPrevious,
	onNext,
	onToday,
	currentUser,
	selectedUser,
	onUserChange,
	canBrowseAll,
	delegatedUsers,
	showMeetings,
	onToggleMeetings,
	showEvents,
	onToggleEvents,
	eventsAvailable,
	showActionItems,
	onToggleActionItems,
	showExternalMeetings,
	onToggleExternalMeetings,
}) => {
	const typeToggles = [
		{
			key: 'meetings',
			label: 'Meetings',
			color: MEETING_LEGEND_COLOR,
			checked: showMeetings,
			onToggle: onToggleMeetings,
		},
		...(eventsAvailable
			? [{ key: 'events', label: 'Events', color: EVENT_COLOR, checked: showEvents, onToggle: onToggleEvents }]
			: []),
		{
			key: 'actionItems',
			label: 'Action Items',
			color: ACTION_ITEM_COLOR,
			checked: showActionItems,
			onToggle: onToggleActionItems,
		},
		{
			key: 'external',
			label: 'External',
			color: EXTERNAL_MEETING_COLOR,
			checked: showExternalMeetings,
			onToggle: onToggleExternalMeetings,
		},
	];
	const selectedLabels = typeToggles.filter(toggle => toggle.checked).map(toggle => toggle.label);
	const triggerLabel =
		selectedLabels.length === 0
			? 'None'
			: selectedLabels.length === typeToggles.length
				? 'All'
				: selectedLabels.length <= 2
					? selectedLabels.join(', ')
					: `${selectedLabels[0]} +${selectedLabels.length - 1}`;
	return (
		<CalendarViewToolbar
			viewMode={viewMode}
			onViewModeChange={onViewModeChange}
			title={title}
			onPrevious={onPrevious}
			onNext={onNext}
			onToday={onToday}
		>
			<Dropdown>
				<DropdownSelectTrigger styles={[styleSheet.typeFilterTrigger]}>{triggerLabel}</DropdownSelectTrigger>
				<DropdownContent align='start'>
					{typeToggles.map(toggle => (
						<TypeCheckboxItem
							key={toggle.key}
							label={toggle.label}
							color={toggle.color}
							checked={toggle.checked}
							onToggle={toggle.onToggle}
						/>
					))}
				</DropdownContent>
			</Dropdown>
			<CalendarUserSelect
				currentUser={currentUser}
				selectedUser={selectedUser}
				onChange={onUserChange}
				canBrowseAll={canBrowseAll}
				delegatedUsers={delegatedUsers}
			/>
		</CalendarViewToolbar>
	);
};
